import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, Play, Trash2, Pen, Type, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';

const SignatureInput = ({ 
  label = "Signature", 
  value = {}, 
  onChange,
  className = ''
}) => {
  const [activeTab, setActiveTab] = useState('type'); // 'type', 'voice', 'draw'
  const [typedSignature, setTypedSignature] = useState(value?.typed || '');
  
  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(value?.audioURL || null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  
  // Handwritten signature states
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawing, setHasDrawing] = useState(false);
  const [canvasDataURL, setCanvasDataURL] = useState(value?.canvasDataURL || null);

  // Initialize canvas with saved signature
  useEffect(() => {
    if (canvasDataURL && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        setHasDrawing(true);
      };
      img.src = canvasDataURL;
    }
  }, [canvasDataURL, activeTab]);

  // Update parent when any signature type changes
  const updateParent = (updates) => {
    const newValue = {
      typed: typedSignature,
      audioURL: audioURL,
      audioBlob: audioBlob,
      canvasDataURL: canvasDataURL,
      ...updates
    };
    if (onChange) {
      onChange(newValue);
    }
  };

  // Handle typed signature change
  const handleTypedChange = (e) => {
    const newTyped = e.target.value;
    setTypedSignature(newTyped);
    updateParent({ typed: newTyped });
  };

  // ============ VOICE RECORDING ============
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioURL(url);
        updateParent({ audioURL: url, audioBlob: blob });
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Unable to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const deleteRecording = () => {
    if (audioURL) {
      URL.revokeObjectURL(audioURL);
    }
    setAudioURL(null);
    setAudioBlob(null);
    setRecordingTime(0);
    updateParent({ audioURL: null, audioBlob: null });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ============ HANDWRITTEN SIGNATURE ============
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  useEffect(() => {
    if (activeTab === 'draw') {
      initCanvas();
    }
  }, [activeTab]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      if (canvas) {
        const dataURL = canvas.toDataURL('image/png');
        setCanvasDataURL(dataURL);
        updateParent({ canvasDataURL: dataURL });
      }
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasDrawing(false);
      setCanvasDataURL(null);
      updateParent({ canvasDataURL: null });
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <Label className="font-semibold text-sm">{label}</Label>
      
      {/* Tab Buttons */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
        <button
          type="button"
          onClick={() => setActiveTab('type')}
          className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'type' 
              ? 'bg-white text-[#f39c12] shadow-sm' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Type className="w-4 h-4" /> Type
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('voice')}
          className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'voice' 
              ? 'bg-white text-[#f39c12] shadow-sm' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Mic className="w-4 h-4" /> Voice
          {audioURL && <span className="w-2 h-2 bg-green-500 rounded-full"></span>}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('draw')}
          className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'draw' 
              ? 'bg-white text-[#f39c12] shadow-sm' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Pen className="w-4 h-4" /> Draw
          {hasDrawing && <span className="w-2 h-2 bg-green-500 rounded-full"></span>}
        </button>
      </div>

      {/* Type Signature */}
      {activeTab === 'type' && (
        <div className="space-y-2">
          <input
            type="text"
            value={typedSignature}
            onChange={handleTypedChange}
            placeholder="Type your full name to sign"
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#f39c12] focus:border-transparent"
            style={{ fontFamily: 'cursive', fontSize: '18px' }}
          />
          {typedSignature && (
            <div className="p-3 bg-gray-50 rounded border">
              <p className="text-xs text-gray-500 mb-1">Signature Preview:</p>
              <p style={{ fontFamily: 'cursive', fontSize: '24px' }}>{typedSignature}</p>
            </div>
          )}
        </div>
      )}

      {/* Voice Recording */}
      {activeTab === 'voice' && (
        <div className="space-y-3 p-4 border rounded-lg bg-gray-50">
          <p className="text-xs text-gray-600">Record your voice as signature verification</p>
          
          {!audioURL ? (
            <div className="flex flex-col items-center gap-3">
              {isRecording ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                    <span className="text-red-600 font-medium">Recording... {formatTime(recordingTime)}</span>
                  </div>
                  <Button 
                    type="button"
                    onClick={stopRecording}
                    variant="destructive"
                    className="flex items-center gap-2"
                  >
                    <Square className="w-4 h-4" /> Stop Recording
                  </Button>
                </>
              ) : (
                <Button 
                  type="button"
                  onClick={startRecording}
                  className="flex items-center gap-2 bg-[#f39c12] hover:bg-[#e67e22]"
                >
                  <Mic className="w-4 h-4" /> Start Recording
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                <span className="text-green-700 font-medium">Voice signature recorded</span>
              </div>
              
              <audio 
                ref={audioRef} 
                src={audioURL} 
                onEnded={() => setIsPlaying(false)}
                className="hidden"
              />
              
              <div className="flex justify-center gap-2">
                {!isPlaying ? (
                  <Button 
                    type="button"
                    onClick={playAudio}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Play className="w-4 h-4" /> Play
                  </Button>
                ) : (
                  <Button 
                    type="button"
                    onClick={stopAudio}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Square className="w-4 h-4" /> Stop
                  </Button>
                )}
                <Button 
                  type="button"
                  onClick={deleteRecording}
                  variant="outline"
                  className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Handwritten Signature */}
      {activeTab === 'draw' && (
        <div className="space-y-2">
          <div className="relative">
            <canvas
              ref={canvasRef}
              width={400}
              height={150}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg bg-white cursor-crosshair touch-none"
              style={{ maxWidth: '100%', height: '150px' }}
            />
            {!hasDrawing && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-gray-400 text-sm" data-no-pdf>Sign here with mouse or touch</p>
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button 
              type="button"
              onClick={clearCanvas}
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" /> Clear
            </Button>
          </div>
        </div>
      )}

      {/* Status Indicators */}
      <div className="flex gap-4 text-xs text-gray-500">
        {typedSignature && (
          <span className="flex items-center gap-1">
            <Type className="w-3 h-3" /> Typed ✓
          </span>
        )}
        {audioURL && (
          <span className="flex items-center gap-1">
            <Mic className="w-3 h-3" /> Voice ✓
          </span>
        )}
        {hasDrawing && (
          <span className="flex items-center gap-1">
            <Pen className="w-3 h-3" /> Drawn ✓
          </span>
        )}
      </div>
    </div>
  );
};

export default SignatureInput;
